import React, { ComponentProps, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Target,
  HowToSaveType,
  History,
  TargetHistoryTransitionData,
  howToSaveCash,
} from 'components/features/target-achievement-support/utils/type';
import {
  ExtraTimeSeriesData,
  TimeSeriesDatum,
} from 'components/features/home/assetStuffing/AssetStuffing';
import { formatDate } from 'utils/formatter';
import { getTargetHistory } from 'config/api';
import { YearlyAICheer } from './rangeTab/YearlyAICheer';
import { RemainAmount } from './rangeTab/RemainAmount';
import { dropDownClassNames } from './dropdownStyle';
import { TransitionChart } from './TransitionChart';
import { ChartLoading } from './Month';
import { DropDown } from '../../../commons/DropDown';
import { toNumber } from 'components/features/target-achievement-support/utils/utils';

const toString = (year: number): string => `${year}`;

const fromString = (
  s: string
): { tag: 'Just'; value: number } | { tag: 'Nothing' } => {
  const num = toNumber(s);
  if (!num) return { tag: 'Nothing' };
  if (num < 2000 || num > 2999) return { tag: 'Nothing' };
  return { tag: 'Just', value: num };
};

const toLabel = (year: number): string => `${year}年`;

const YearDropDown = ({
  setState,
  state,
  states,
}: {
  state: number;
  setState: React.Dispatch<React.SetStateAction<number>>;
  states: number[];
}): React.ReactElement => {
  return (
    <>
      <DropDown
        {...{
          classNames: {
            ...dropDownClassNames,
            select: (s: string): string => {
              return ((s_: string): string =>
                s_
                  .split(' ')
                  .map(c => (/^mr.*/.test(c) ? 'mr-6' : c))
                  .join(' '))(
                (dropDownClassNames?.select ?? ((a: string): string => a))(s)
              );
            },
          },
          dropdownIcon: 'arrow_drop_down',
          toString_: toString,
          fromString,
          setState,
          state,
          states,
          toLabel,
        }}
      />
    </>
  );
};

const YearRemainAmount = ({
  currentAmount,
  year,
  targetAmount,
  className,
}: Omit<ComponentProps<typeof RemainAmount>, 'limit' | 'period'> & {
  year: number;
}): React.ReactElement => {
  return (
    <>
      <RemainAmount
        {...{
          currentAmount,
          limit: { value: year, period: 'year' },
          targetAmount,
          className,
        }}
      />
    </>
  );
};

const YearAICheer = ({
  percentage,
  title,
  achieved,
  currentAmount,
  targetAmount,
  targetStartDate,
  targetEndDate,
  requiredYield,
  goHowToIncreaseAccumulationAmount,
  className,
}: Omit<ComponentProps<typeof YearlyAICheer>, 'period'> & {
  className?: string;
}): React.ReactElement => {
  return (
    <div
      className={`rounded-lg flex flex-row items-start gap-4 border border-bluegray-300 bg-bluegray-100 p-4 ${className ??
        ''}`}>
      <YearlyAICheer
        {...{
          percentage,
          title,
          achieved,
          currentAmount,
          targetAmount,
          targetStartDate,
          targetEndDate,
          requiredYield,
          goHowToIncreaseAccumulationAmount,
        }}
      />
    </div>
  );
};

type Props = {
  goHowToIncreaseAccumulationAmount: () => void;
  target: Target;
  targetHowToSaveType: HowToSaveType;
};

export const Year = (props: Props): React.ReactElement => {
  const [state, setState] = useState<number>(new Date().getFullYear());

  const { data: history, refetch, isLoading, isRefetching } = useQuery(
    'yearTargethistory',
    () => getTargetHistoryAPI(props.target.id, state),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    refetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const isFailed = history === null || history === undefined;

  const aiCheer = useMemo(() => {
    if (isFailed) return null;

    const achieved = props.target.achieved;
    const targetStartDate = new Date(props.target.startDate);
    const targetEndDate = new Date(props.target.endDate);
    const requiredYield =
      props.target.reserveAmount !== null &&
      props.target.excludeAmount !== null &&
      props.target.accountList !== null
        ? null
        : props.targetHowToSaveType === howToSaveCash
        ? null
        : (annualYield => Math.ceil(annualYield * 1000) / 10)(
            props.target.yield
          );
    const currentAmount = history.achievementAmount;
    const targetAmountForDisplay = history.targetAmount;

    const num = (currentAmount / targetAmountForDisplay) * 100;
    const percentage = Number.isNaN(num) ? 0 : num;

    return (
      <YearAICheer
        percentage={percentage}
        title={props.target.title}
        achieved={achieved}
        currentAmount={currentAmount}
        targetAmount={targetAmountForDisplay}
        targetStartDate={targetStartDate}
        targetEndDate={targetEndDate}
        requiredYield={requiredYield}
        goHowToIncreaseAccumulationAmount={
          props.goHowToIncreaseAccumulationAmount
        }
        className="mt-4 mx-4"
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFailed]);

  const chartIncorrectData = useMemo(() => {
    if (isFailed) return null;

    const transitions = history.transitions as TargetHistoryTransitionData[];
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: transitions.map(it => {
        return {
          value: it.incorrect,
          date: new Date(it.date),
        } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [history, isFailed]);

  if (isFailed || isLoading || isRefetching || chartIncorrectData === null)
    return <ChartLoading />;

  const currentAmount = history.achievementAmount;

  const targetAmountForDisplay = history.targetAmount;

  const { end, start } = {
    start: new Date(history.from),
    end: new Date(history.to),
  };

  const rangeStr = ((): string => {
    const toString = (date: Date): string =>
      `${date.getMonth() + 1}月${date.getDate()}日`;
    const start_ =
      start.getFullYear() === state ? start : new Date(state, 1, 1);
    const end_ = end.getFullYear() === state ? end : new Date(state, 12, 31);
    return `${toString(start_)}〜${toString(end_)}`;
  })();

  const startDate = new Date(props.target.startDate);
  const endDate = new Date(props.target.startDate);

  const states = Array.from({
    length: new Date().getFullYear() - startDate.getFullYear() + 1,
  }).map((_, i) => startDate.getFullYear() + i);

  const chartData =
    history.transitions[history.transitions.length - 1]?.benchmarkAmount ??
    history.targetAmount;

  const transitions = history.transitions as TargetHistoryTransitionData[];

  return (
    <>
      <div>
        <div className="bg-white py-4">
          <div className="flex flex-col items-center justify-center">
            <div className="flex flex-row items-center justify-center ml-3">
              <YearDropDown {...{ state, setState, states }} />
            </div>
            <div className="text-bluegray-700 text-sm">{rangeStr}</div>
          </div>
          <YearRemainAmount
            {...{
              currentAmount,
              year: state,
              targetAmount: targetAmountForDisplay,
            }}
            className="mt-2"
          />
          <TransitionChart
            dateFormatter={date => `${formatDate(date, 'yyyy/MM')}`}
            targetDate={endDate}
            data={{
              dataRange: {
                from: startDate,
                to: endDate,
              },
              achievements: transitions.map(item => {
                return {
                  achievement: item.assetAmount,
                  date: new Date(item.date),
                };
              }),
              benchmarks: transitions.map(item => ({
                benchmark: item.benchmarkAmount,
                date: new Date(item.date),
              })),
              targetAmount: chartData,
              extraData: chartIncorrectData,
            }}
          />
          {aiCheer}
        </div>
      </div>
    </>
  );
};

const getTargetHistoryAPI = async (id: string, state: number) => {
  const period = `${state}`;
  const ret = await getTargetHistory(id, period);

  return {
    id: ret.ID,
    achievementAmount: ret.AchievementAmount,
    targetAmount: ret.TargetAmount,
    from: ret.From,
    to: ret.To,
    transitions: ret.Transitions.map(item => {
      return {
        assetAmount: item.AssetAmount,
        benchmarkAmount: item.BenchmarkAmount,
        date: item.Date,
        incorrect: item.Incorrect,
      };
    }),
  } as History;
};
